import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Truck, Pencil, Zap } from 'lucide-react';
import Button from '../ui/Button';
import nurseryService from '../../services/nursery';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import { OrderDespatchFields } from './OrderDespatchFields';
import {
  type NurseryCustomerAddress,
  type NurseryDeliveryZone,
  type NurseryOrder,
  type OrderDespatchPayload,
} from '../../types/nursery';

/**
 * The order page's Despatch card: how, when and where the order goes, and —
 * for a nursery manager — the place to change it until it has shipped.
 */

// Mirrors DESPATCH_LOCKED_STATUSES in HHNodeServer nurseryService.
const LOCKED = ['shipped', 'completed', 'cancelled'];

export interface OrderDespatchCardProps {
  order: NurseryOrder;
  timezone: string;
  /** Reload the order: the save response carries none of its joined fields. */
  onSaved: () => void;
}

function windowText(order: NurseryOrder, tz: string, t: TFunction<'nursery', 'OrderDespatchCard'>): string | null {
  const from = order.despatch_from ? formatLocalDateInTz(order.despatch_from, tz) : null;
  const by = order.despatch_by ? formatLocalDateInTz(order.despatch_by, tz) : null;
  if (from && by) return t('windowFromTo', { from, by });
  if (by) return t('windowBy', { by });
  if (from) return t('windowFrom', { from });
  return null;
}

export function OrderDespatchCard({ order, timezone, onSaved }: OrderDespatchCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderDespatchCard' });
  const { hasAccess: canManage } = useStockSeat('manage');
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<OrderDespatchPayload>({});
  const [addresses, setAddresses] = useState<NurseryCustomerAddress[]>([]);
  const [usesCrossDock, setUsesCrossDock] = useState<boolean | undefined>(undefined);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const method = order.despatch_method ?? 'collection';
  const despatchWindow = windowText(order, timezone, t);

  const startEdit = async () => {
    setError(null);
    setValue({
      despatch_method: method,
      despatch_from: order.despatch_from ?? null,
      despatch_by: order.despatch_by ?? null,
      priority: order.priority ?? 'normal',
      collection_at: order.collection_at ?? null,
      delivery_address_id: order.delivery_address_id ?? null,
      // Left undefined: the server keeps the zone unless the address or method
      // moves, and re-resolves it when they do.
    });
    setEditing(true);
    try {
      const [zoneRes, custRes] = await Promise.all([
        nurseryService.getDeliveryZones(),
        order.customer_id ? nurseryService.getCustomer(order.customer_id) : Promise.resolve(null),
      ]);
      if (zoneRes.success) setZones(zoneRes.data || []);
      if (custRes?.success) {
        setAddresses(custRes.data.addresses || []);
        setUsesCrossDock(custRes.data.account?.uses_cross_dock);
      }
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: OrderDespatchPayload = { ...value };
      if (payload.delivery_zone_id === undefined) delete payload.delivery_zone_id;
      const res = await nurseryService.updateOrder(order.id, payload);
      if (res.success) {
        onSaved();
        setEditing(false);
      } else {
        setError(errorMessage(res, t('saveFailed')));
      }
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('title')}</h3>
        {canManage && !editing && !LOCKED.includes(order.status) && (
          <Button variant="outline" size="sm" onClick={startEdit}>
            <Pencil className="h-4 w-4" /> {t('edit')}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">{error}</div>
      )}

      {editing ? (
        <>
          <OrderDespatchFields
            value={value}
            onChange={setValue}
            addresses={addresses}
            zones={zones}
            timezone={timezone}
            customerId={order.customer_id}
            currentZoneName={order.delivery_zone_name}
            usesCrossDock={usesCrossDock}
          />
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>{t('cancel')}</Button>
            <Button size="sm" onClick={save} disabled={saving}>{saving ? t('saving') : t('save')}</Button>
          </div>
        </>
      ) : (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-neutral-500">{t('howItGoes')}</dt>
            <dd className="text-neutral-800 flex flex-wrap items-center gap-2">
              <Truck className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              {t(`method.${method}`)}
              {order.priority === 'asap' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2 py-0.5 text-xs font-semibold">
                  <Zap className="h-3 w-3" /> {t('asap')}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-neutral-500">{t('despatchWindow')}</dt>
            <dd className="text-neutral-800">{despatchWindow ?? <span className="text-neutral-400">{t('notSet')}</span>}</dd>
          </div>
          {method === 'collection' ? (
            <div>
              <dt className="text-xs text-neutral-500">{t('collectionTime')}</dt>
              <dd className="text-neutral-800">
                {order.collection_at
                  ? formatDateTimeInTz(order.collection_at, timezone)
                  : <span className="text-neutral-400">{t('notBooked')}</span>}
              </dd>
            </div>
          ) : method === 'cross_dock' ? (
            // To the retailer's dock, which sends it on to the store. The route
            // and delivery number are what its cross dock schedule gave it.
            <>
              <div className="min-w-0">
                <dt className="text-xs text-neutral-500">{t('xdockRoute')}</dt>
                <dd className="text-neutral-800 break-words">
                  {order.xdock_route
                    ? `${order.xdock_route}${order.xdock_del_seq ? t('deliverySeq', { seq: order.xdock_del_seq }) : ''}`
                    : <span className="text-amber-700">{t('noXdock')}</span>}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-neutral-500">{t('pickDay')}</dt>
                <dd className="text-neutral-800 break-words">
                  {order.xdock_schedule_id ? t('pickDaySchedule') : t('pickDayHand')}
                </dd>
              </div>
            </>
          ) : (
            <>
              <div className="min-w-0">
                <dt className="text-xs text-neutral-500">{t('deliveryAddress')}</dt>
                <dd className="text-neutral-800 whitespace-pre-line break-words">
                  {order.delivery_address || <span className="text-amber-700">{t('noAddress')}</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">{t('zone')}</dt>
                <dd className="text-neutral-800 break-words">
                  {order.delivery_zone_name || <span className="text-amber-700">{t('noZone')}</span>}
                </dd>
              </div>
            </>
          )}
          <div>
            <dt className="text-xs text-neutral-500">{t('load')}</dt>
            <dd className="text-neutral-800">
              {order.load_id ? (
                <Link to={`/nursery/loads/${order.load_id}`} className="text-primary-700 hover:underline font-medium">
                  {order.load_number}
                  {order.load_despatch_date ? `, ${formatLocalDateInTz(order.load_despatch_date, timezone)}` : ''}
                </Link>
              ) : (
                <span className="text-neutral-400">{t('noLoad')}</span>
              )}
            </dd>
          </div>
          {order.parent_order_number && (
            <div>
              <dt className="text-xs text-neutral-500">{t('backorderOf')}</dt>
              <dd className="text-neutral-800">
                {order.parent_order_id ? (
                  <Link to={`/nursery/orders/${order.parent_order_id}`} className="text-primary-700 hover:underline font-medium">
                    {order.parent_order_number}
                  </Link>
                ) : order.parent_order_number}
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

export default OrderDespatchCard;
